<template>
  <div ref="elementSetting" class="mb-4">
    <div class="setting-header">
      <label :for="setting.name" class="mb-0">
        {{ setting.label }}
      </label>
    </div>

    <SettingsMessage v-if="setting.message" :message="setting.message" />

    <!-- Show empty state if no Element found -->
    <PromotionalBanner
      v-if="state.matches('unselected')"
      title="Make your messages pop 💥"
      subtitle="Upload or choose one of our amazing images! Proven to increase conversions
        by up to 50%!"
      button-label="Choose Now"
      img-path="heros/sale.jpg"
      @click="send('SELECT')"
    />

    <!-- Show existing Element with ability to change -->
    <ElementSelectorCard
      v-if="state.matches('selected')"
      :element="state.context.selectedElement"
      @change-click="onChangeClick"
      @remove-click="onRemoveClick"
    />

    <!-- Show all Elements the user can select -->
    <ElementsList
      v-if="state.matches('selecting')"
      :key="elementTypeId"
      :element-types="setting.elementTypes"
      :element-type-id="elementTypeId"
      :selected-element-id="selectedElementId"
      @element-click="onElementClick"
      @close-click="onCloseClick"
      @remove-click="onRemoveClick"
    />
  </div>
</template>

<script>
import clone from "just-clone";
import head from "lodash.head";
import { useMachine } from "@xstate/vue";
import elementMachine from "@/components/Elements/element-machine";
import { getAccountId } from "@/lib/auth";
import SettingsMessage from "./SettingsMessage";
import ElementSelectorCard from "@/components/Elements/ElementSelectorCard";
import ElementsList from "@/components/Elements/ElementsList/ElementsList";
import PromotionalBanner from "@/components/UI/PromotionalBanner";
import { watchState } from "@/utils/machine-helpers";

export default {
  name: "ElementSetting",
  setup(props) {
    // Iniate the machine with context from our props...
    const { state, send } = useMachine(elementMachine, {
      context: {
        selectedElement: props.setting?.selectedElement ?? null,
      },
    });

    // watchState(state, "Element");

    return {
      state,
      send,
    };
  },
  components: {
    ElementsList,
    ElementSelectorCard,
    PromotionalBanner,
    SettingsMessage,
  },
  props: {
    setting: {
      type: Object,
      required: true,
      id: {
        required: true,
        type: String,
      },
      images: {
        default: () => [],
        type: Array,
      },
    },
    saving: {
      default: false,
      type: Boolean,
    },
  },
  computed: {
    elementTypesExist() {
      return this.setting.elementTypes && this.setting.elementTypes.length > 1;
    },
    elementTypeId() {
      const { selectedElement } = this.state?.context;
      // If there's a selected element and it has a type ID then return it...
      const selectedElementTypeId = selectedElement?.type?.elementTypeId;

      if (selectedElementTypeId) return selectedElementTypeId;

      // If not and there is only one elementType in the elementTypes array then set that as the default type...
      // FUTURE: If we add categories in the future then don't set a default by using this instead
      // const elementType = this.setting?.elementTypes?.length === 1 && head(this.setting.elementTypes);
      const elementType = head(this.setting.elementTypes);

      return elementType?.elementTypeId ?? "";
    },
    selectedElementId() {
      return this.state?.context?.selectedElement?.elementId ?? "";
    },
  },
  mounted() {
    this.setDefaultElementValue();
  },
  methods: {
    onChangeClick() {
      this.send("SELECT");
      this.$refs.elementSetting.scrollIntoView();
    },
    onCloseClick() {
      this.send("CLOSE");
    },
    onElementClick(element) {
      const { elementId } = element;

      // this.showElements = false;
      this.send("SELECTED", { data: { selectedElement: element } });

      // Update selectedElementExists to show the selected element...

      // Update the setting with the new element...
      this.updateSetting(element, elementId);

      // Track the event...
      this.$gtm.trackEvent({
        event: "element-selected",
        accountId: getAccountId(),
        elementId,
      });
    },
    onRemoveClick() {
      // Update selectedElementExists to show the EmptyElementSelector...
      this.send("REMOVE");

      // Update the setting without any data to remove the item...
      this.updateSetting();
    },
    setDefaultElementValue() {
      const elementValue = this.setting?.elementValue;

      // This is needed as the backend won't accept null for the elementValue...
      if (elementValue === null) {
        this.setting.elementValue = "";
      }
    },
    updateSetting(elementItem = null, id = "") {
      const setting = clone(this.setting);

      // Update the elementValue...
      setting.elementValue = id;

      // Update the selectedElement, even if elementItem is null as we're removing the element...
      setting.selectedElement = elementItem;

      // Emit the update...
      this.$emit("setting-updated", setting, id);
    },
  },
};
</script>

<style scoped>
.setting-header {
  @apply flex items-center mb-4;
}

.close-button {
  @apply text-sm text-brand;

  &:focus {
    @apply outline-none;
  }
}
</style>
